import { create } from 'zustand'
import { persist, subscribeWithSelector } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import { photoMemory } from './photoMemoryStore'
import { uploadWorkerRef } from '../services/useUploadWorker'
import { buildAuthHeader, registerAppStore } from '../utils/auth'
import { BACKEND } from '../utils/config'
import { makeInspectionKey, tryMakeInspectionKey, getMrcContractId } from '../utils/inspectionKey'

// ─── State initial ─────────────────────────────────────────────────────────────
const INITIAL_INSPECTION = {
  contract:            null,
  type:                null,
  inspectionKey:       null,    // ✅ v5.0 — clé déterministe "dep-{numero}" | "retour-{numero}"
  damages:             [],
  mileage:             null,
  fuel:                null,
  signatureAgent:      null,
  signatureClient:     null,
  completedAt:         null,
  pendingPhotoSlot:    null,
  pendingPhotoData:    null,
  photosChecked:       false,
  accidentDeclaration: null,
  startedAt:           null,
  deviceInfo:          null,
  securityCheck:       null,
}

function getDeviceInfo() {
  try {
    return { ua: navigator.userAgent, platform: navigator.platform, ts: new Date().toISOString() }
  } catch { return null }
}

// ── Helper : UUID ─────────────────────────────────────────────────────────────
// ✅ FIX — Fallback pour les contextes non sécurisés (HTTP sur IP locale)
function uuid() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

// ── Helpers de sauvegarde brouillon ─────────────────────────────────────────────

/**
 * Sauvegarde immédiate dans IndexedDB — sans délai.
 * ✅ v5.0 — utilise inspectionKey au lieu de contractId brut.
 */
async function persistDraftNow(inspection, step) {
  if (!inspection?.contract?.id || !inspection?.type) return

  // ✅ v5.0 — utiliser inspectionKey si disponible, sinon le construire
  const inspectionKey = inspection.inspectionKey
    ?? tryMakeInspectionKey(inspection.type, inspection.contract)
  if (!inspectionKey) {
    console.warn('[Draft] ⚠️ Impossible de construire inspectionKey — draft non sauvegardé')
    return
  }
  const type = inspection.type
  if (type !== 'depart' && type !== 'retour') return

  try {
    const { offlinePhotoStore } = await import('./offlinePhotoStore')
    const payload = {
      ...inspection,
      step,
      savedAt: new Date().toISOString(),
    }
    await offlinePhotoStore.saveDraft(inspectionKey, type, payload)
    console.log(`[Draft] ✅ Sauvegarde — ${inspectionKey} @ ${step}`)
  } catch (err) {
    console.error('[Draft] ❌ Échec sauvegarde :', err?.message ?? err)
  }
}

// ── Debounce helpers ─────────────────────────────────────────────────────────

/** @type {Map<string, ReturnType<typeof setTimeout>>} */
const draftTimers = new Map()

/**
 * Sauvegarde différée (debounce).
 * ✅ v5.0 — utilise inspectionKey comme clé du timer.
 */
function debouncedDraftSave(inspection, step, delayMs = 300) {
  if (!inspection?.contract?.id || !inspection?.type) return
  const inspectionKey = inspection.inspectionKey
    ?? tryMakeInspectionKey(inspection.type, inspection.contract)
  if (!inspectionKey) return
  
  const existing = draftTimers.get(inspectionKey)
  if (existing) clearTimeout(existing)

  const timer = setTimeout(() => {
    persistDraftNow(inspection, step)
    draftTimers.delete(inspectionKey)
  }, delayMs)

  draftTimers.set(inspectionKey, timer)
}

function autoSaveDraft(step) {
  if (!DRAFT_SAFE_STEPS.includes(step)) return
  const inspection = useAppStore.getState().inspection
  if (!inspection?.contract?.id || !inspection?.type) return
  debouncedDraftSave(inspection, step, 300)
}

// ── _processUploadQueue supprimé (v1.7.0) ──────────────────────────────────
// Tout est désormais centralisé dans useUploadWorker.ts.
// uploadWorkerRef.current est toujours disponible dès que App monte <AppWorkers />.

const DRAFT_SAFE_STEPS = ['recap', 'choose', 'photos', 'fuel', 'security', 'signatures']

export const useAppStore = create(
  subscribeWithSelector(
    persist(
      immer((set, get) => ({

        credentials:          null,
        isAuthenticated:      false,
        selectedAgency:       null,
        contracts:            [],
        lastSync:             null,
        step:                 'login',
        inspection:           { ...INITIAL_INSPECTION },
        completedInspections: {},
        uploadQueue:          [],
        selectionQueue:       [],   // Phase 2 — contrats prêts pour inspection retour

        setCredentials: (creds) =>
          set(state => {
            state.credentials     = creds
            state.isAuthenticated = !!creds
            state.step = creds
              ? (state.contracts?.length > 0 ? 'contracts' : 'sync')
              : 'login'
          }),

        logout: () =>
          set(state => {
            state.credentials     = null
            state.isAuthenticated = false
            state.step            = 'login'
            state.inspection      = { ...INITIAL_INSPECTION }
            photoMemory.clear()
          }),

        setContracts: (contracts) =>
          set(state => {
            state.contracts = contracts
            state.lastSync  = new Date().toISOString()
          }),

        setStep: (step) =>
          set(state => {
            state.step = step
            autoSaveDraft(step)
          }),

        setSelectedAgency: (agency) => set(state => { state.selectedAgency = agency }),

        startInspection: (contract, type) =>
          set(state => {
            // ✅ v5.0 — Construire l'inspectionKey DÈS le démarrage
            const inspectionKey = tryMakeInspectionKey(type, contract)
            if (!inspectionKey) {
              console.error('[startInspection] ❌ Impossible de démarrer : numeroDocument manquant')
              return
            }

            // ✅ Nettoyer le brouillon de l'inspection précédente si elle existe
            const prev = state.inspection
            if (prev?.inspectionKey) {
              const prevKey = prev.inspectionKey
              const prevType = prev.type
              if (prevType === 'depart' || prevType === 'retour') {
                import('./offlinePhotoStore').then(({ offlinePhotoStore }) => {
                  offlinePhotoStore.clearDraft(prevKey, prevType).catch(() => {})
                }).catch(() => {})
              }
            }

            // ✅ v2.0 — Verrouiller photoMemory sur cette inspection
            photoMemory.setActiveInspection(inspectionKey)

            state.inspection = {
              ...INITIAL_INSPECTION,
              contract,
              type,
              inspectionKey,
              startedAt:  new Date().toISOString(),
              deviceInfo: getDeviceInfo(),
            }
            state.step = 'recap'
            autoSaveDraft('recap')
          }),

        resumeInspection: (draft) =>
          set(state => {
            if (!draft || !draft.contract) return
            state.inspection = {
              ...INITIAL_INSPECTION,
              contract:   draft.contract,
              type:       draft.type,
              mileage:    draft.mileage,
              fuel:       draft.fuel,
              damages:    draft.damages || [],
              startedAt:           draft.startedAt || new Date().toISOString(),
              deviceInfo:          getDeviceInfo(),
              securityCheck:       draft.securityCheck,
              signatureAgent:      draft.signatureAgent,
              signatureClient:     draft.signatureClient,
              accidentDeclaration: draft.accidentDeclaration,
              photosChecked:       draft.photosChecked,
            }
            const targetStep = DRAFT_SAFE_STEPS.includes(draft.step) ? draft.step : 'recap'
            state.step = targetStep
            autoSaveDraft(targetStep)
          }),

        initInspection: (contract, type) =>
          set(state => {
            // ✅ v5.0 — construire inspectionKey
            const inspectionKey = tryMakeInspectionKey(type, contract)
            if (inspectionKey) {
              photoMemory.setActiveInspection(inspectionKey)
            } else {
              photoMemory.clear()
            }
            state.inspection = {
              ...INITIAL_INSPECTION,
              contract,
              type,
              inspectionKey,
              startedAt:  new Date().toISOString(),
              deviceInfo: getDeviceInfo(),
            }
            autoSaveDraft(state.step)
          }),

        updateInspection: (data) =>
          set(state => {
            Object.assign(state.inspection, data)
            autoSaveDraft(state.step)
          }),

        restoreFromDraft: async (draft) => {
          // ✅ v5.0 — construire inspectionKey depuis le draft
          const inspectionKey = tryMakeInspectionKey(draft.type, draft.contract)
            ?? draft.contractId  // fallback legacy si le draft a été créé avant v5.0

          if (inspectionKey) {
            photoMemory.setActiveInspection(inspectionKey)
          } else {
            photoMemory.clear()
          }

          set(state => {
            state.inspection = {
              ...INITIAL_INSPECTION,
              contract:   draft.contract,
              type:       draft.type,
              inspectionKey,
              mileage:    draft.mileage,
              fuel:       draft.fuel,
              damages:    draft.damages ?? [],
              startedAt:  draft.startedAt,
              deviceInfo: getDeviceInfo(),
            }
            state.step = DRAFT_SAFE_STEPS.includes(draft.step) ? draft.step : 'recap'
          })

          try {
            if (!inspectionKey) return
            const { offlinePhotoStore } = await import('./offlinePhotoStore')
            const photoMap = await offlinePhotoStore.getPhotos(inspectionKey, draft.type)
            for (const [zone, photo] of Object.entries(photoMap)) {
              photoMemory.setPhoto(zone, photo.dataUrl, photo.originalDataUrl)
            }
            set(state => { state._photoVersion = (state._photoVersion ?? 0) + 1 })
          } catch (e) {
            console.warn('[restoreFromDraft] erreur rechargement photos:', e)
          }

          // ✅ Phase 1 — Cache-first : photos de départ depuis IndexedDB, puis réseau
          if (draft.type === 'retour' && draft.contract?.id) {
            try {
              const { offlinePhotoStore } = await import('./offlinePhotoStore')
              const contractId = draft.contract.rawId ?? draft.contract.id

              // 1. Tenter le cache local
              const cached = await offlinePhotoStore.getDepartureCache(contractId)
              if (cached && typeof cached === 'object' && !Array.isArray(cached) && Object.keys(cached).length > 0) {
                // ✅ FIX — Vérifier que le cache contient des URLs (string), pas des metadata MRC
                const firstValue = Object.values(cached)[0]
                if (typeof firstValue === 'string' && firstValue.startsWith('http')) {
                  photoMemory.setDepartPhotosFromUrls(cached, contractId)
                  set(state => { state._photoVersion = (state._photoVersion ?? 0) + 1 })
                  console.log('[restoreFromDraft] Photos départ depuis cache local:', Object.keys(cached).length)
                  return
                } else {
                  console.warn('[restoreFromDraft] Cache départ invalide (format incorrect), re-fetch...')
                  await offlinePhotoStore.clearDepartureCache(contractId)
                  // Fall through to network fetch below
                }
              }

              // 2. Fallback réseau + mise en cache
              // ✅ FIX — Utiliser l'endpoint Supabase (format {zone: url}) et non MRC (format [{id, libelle}])
              const credentials = get().credentials
              if (credentials) {
                const authHeader = buildAuthHeader(credentials)
                const res = await fetch(`${BACKEND}/api/edl/${contractId}/photos?type=depart`, {
                  headers: { 'X-MRC-Auth': authHeader },
                })
                if (res.ok) {
                  const data = await res.json()
                  if (data?.photos && typeof data.photos === 'object' && !Array.isArray(data.photos)
                      && Object.keys(data.photos).length > 0) {
                    photoMemory.setDepartPhotosFromUrls(data.photos, contractId)
                    set(state => { state._photoVersion = (state._photoVersion ?? 0) + 1 })
                    // Sauvegarder dans le cache pour le prochain accès offline
                    await offlinePhotoStore.saveDepartureCache(contractId, data.photos)
                    console.log('[restoreFromDraft] Photos départ depuis réseau + cache:', Object.keys(data.photos).length)
                  }
                }
              }
            } catch (e) {
              console.warn('[restoreFromDraft] erreur rechargement photos départ:', e)
            }
          }
        },

        setPhoto: (slotId, annotatedDataUrl, originalDataUrl = null) => {
          photoMemory.setPhoto(slotId, annotatedDataUrl, originalDataUrl)
          set(state => { state._photoVersion = (state._photoVersion ?? 0) + 1 })
          const inspection = get().inspection
          // ✅ v5.0 — utiliser inspectionKey au lieu de rawId/id
          const inspectionKey = inspection?.inspectionKey
          const type          = inspection?.type
          if (inspectionKey && (type === 'depart' || type === 'retour')) {
            import('./offlinePhotoStore').then(({ offlinePhotoStore }) => {
              offlinePhotoStore.savePhotos(
                inspectionKey,
                { [slotId]: {
                    dataUrl:         annotatedDataUrl,
                    originalDataUrl: originalDataUrl ?? annotatedDataUrl,
                }},
                type
              ).catch(() => {})
            }).catch(() => {})
          }
        },

        setDepartPhoto: (slotId, annotatedDataUrl, originalDataUrl = null) => {
          photoMemory.setDepartPhoto(slotId, annotatedDataUrl, originalDataUrl)
          set(state => { state._photoVersion = (state._photoVersion ?? 0) + 1 })
        },

        updatePhotoAnnotation: (slotId, annotatedDataUrl) => {
          const existing = photoMemory.getPhoto(slotId)
          if (existing) {
            photoMemory.setPhoto(slotId, annotatedDataUrl, existing.originalDataUrl)
            set(state => { state._photoVersion = (state._photoVersion ?? 0) + 1 })
          }
        },

        getPhotos:       () => photoMemory.getPhotos(),
        getDepartPhotos: () => photoMemory.getDepartPhotos(),
        getPhoto:        (slotId) => photoMemory.getPhoto(slotId),
        getDepartPhoto:  (slotId) => photoMemory.getDepartPhoto(slotId),

        addDamage: (damage) =>
          set(state => {
            state.inspection.damages.push({
              ...damage,
              id:        damage.id ?? uuid(),
              createdAt: new Date().toISOString(),
            })
            autoSaveDraft(state.step)
          }),

        updateDamage: (id, patch) =>
          set(state => {
            const idx = state.inspection.damages.findIndex(d => d.id === id)
            if (idx !== -1) {
              Object.assign(state.inspection.damages[idx], patch)
              autoSaveDraft(state.step)
            }
          }),

        removeDamage: (idx) =>
          set(state => {
            state.inspection.damages.splice(idx, 1)
            autoSaveDraft(state.step)
          }),

        removeDamageById: (id) =>
          set(state => {
            state.inspection.damages = state.inspection.damages.filter(d => d.id !== id)
            autoSaveDraft(state.step)
          }),

        removeDamagesBySlot: (slotId) =>
          set(state => {
            state.inspection.damages = state.inspection.damages.filter(d => d.slotId !== slotId)
            autoSaveDraft(state.step)
          }),

        setPendingPhoto: (slot, data) =>
          set(state => {
            state.inspection.pendingPhotoSlot = slot
            state.inspection.pendingPhotoData = data
          }),

        clearPendingPhoto: () =>
          set(state => {
            state.inspection.pendingPhotoSlot = null
            state.inspection.pendingPhotoData = null
          }),

        addToUploadQueue: (item) =>
          set(state => {
            const exists = state.uploadQueue.some(i => i.contractId === item.contractId)
            if (exists) {
              console.warn(`[Queue] addToUploadQueue: ${item.contractId} déjà présent — ignoré`)
              return
            }
            state.uploadQueue.push({
              ...item,
              id:              item.id ?? uuid(),
              queuedAt:        new Date().toISOString(),
              retries:         0,
              status:          'pending',
              status_mrc:      item.status_mrc      ?? 'pending',   // ✅ FIX — initialisé explicitement
              status_onedrive: item.status_onedrive ?? 'pending',   // ✅ prêt pour OneDrive Phase 3
            })
          }),

        removeFromUploadQueue: (contractId) =>
          set(state => {
            state.uploadQueue = state.uploadQueue.filter(i => i.contractId !== contractId)
          }),

        markQueueItemFailed: (itemId) =>
          set(state => {
            const item = state.uploadQueue.find(i => i.id === itemId)
            if (item) item.status = 'failed'
          }),

        retryQueueItem: (itemId) =>
          set(state => {
            const item = state.uploadQueue.find(i => i.id === itemId)
            if (item) {
              item.status = 'pending'
              item.retries = 0
            }
          }),

        incrementQueueRetry: (id) =>
          set(state => {
            const item = state.uploadQueue.find(i => i.id === id)
            if (item) item.retries += 1
          }),

        processUploadQueue: (contractId) => {
          if (uploadWorkerRef.current) {
            return uploadWorkerRef.current(contractId)
          }
          console.warn('[processUploadQueue] Worker non monté — opération ignorée')
        },

        // ── Phase 2 — Selection Queue (Vagues de retours) ────────────────────
        // selectionQueue = contrats prêts pour inspection (lecture seule)
        // uploadQueue    = EDL terminés en attente de sync (écriture)
        // Règle : un contractId ne peut pas être dans les deux en même temps.

        addToSelectionQueue: (contract) =>
          set(state => {
            const contractId = contract.rawId ?? contract.id
            // Guard R2 : refuser si déjà dans uploadQueue
            if (state.uploadQueue.some(i => i.contractId === String(contractId))) {
              console.warn(`[SelectionQueue] ${contractId} déjà dans uploadQueue — ignoré`)
              return
            }
            // Guard : refuser si déjà dans selectionQueue
            if (state.selectionQueue.some(c => (c.rawId ?? c.id) === contractId)) {
              console.warn(`[SelectionQueue] ${contractId} déjà sélectionné — ignoré`)
              return
            }
            state.selectionQueue.push({
              ...contract,
              _selectedAt: new Date().toISOString(),
              _cacheReady: false,   // devient true après téléchargement des photos
            })
          }),

        removeFromSelectionQueue: (contractId) =>
          set(state => {
            state.selectionQueue = state.selectionQueue.filter(
              c => (c.rawId ?? c.id) !== contractId
            )
          }),

        markSelectionCacheReady: (contractId) =>
          set(state => {
            const item = state.selectionQueue.find(c => (c.rawId ?? c.id) === contractId)
            if (item) item._cacheReady = true
          }),

        clearSelectionQueue: () =>
          set(state => {
            state.selectionQueue = []
          }),

        completeInspection: () =>
          set(state => {
            state.inspection.completedAt = new Date().toISOString()
            state.step = 'done'
            autoSaveDraft('done')
          }),

        saveImmediate: async (contractId, type, payload) => {
          // ✅ v5.0 — utiliser inspectionKey
          const inspectionKey = payload?.inspectionKey
            ?? tryMakeInspectionKey(type, payload?.contract ?? {})
          const keyForTimer = inspectionKey || `${contractId}:${type}`

          // Annuler le debounce en cours pour ce contrat
          const existing = draftTimers.get(keyForTimer)
          if (existing) {
            clearTimeout(existing)
            draftTimers.delete(keyForTimer)
          }
          // ✅ FIX — injecter inspectionKey pour que persistDraftNow l'utilise
          await persistDraftNow(
            {
              ...payload,
              contract: { ...(payload.contract ?? {}), id: contractId },
              type,
              inspectionKey,
            },
            'done'
          )
        },

        saveCompletedInspection: (id, data) =>
          set(state => {
            const { photos, departPhotos, ...safeData } = data
            state.completedInspections[id] = {
              ...safeData,
              photoCount: photoMemory.getPhotoCount(),
              savedAt:    new Date().toISOString(),
            }
          }),

        resetInspection: () =>
          set(state => {
            const { inspection } = state
            // ✅ v5.0 — utiliser inspectionKey pour le cleanup
            if (inspection?.inspectionKey && inspection?.type) {
              const inspectionKey = inspection.inspectionKey
              const type          = inspection.type
              if (type === 'depart' || type === 'retour') {
                import('./useEdlDraftStore').then(({ edlDraftStore }) => {
                  edlDraftStore.clear(inspectionKey, type).catch(() => {})
                }).catch(() => {})
              }
            }
            photoMemory.clear()
            state.inspection = { ...INITIAL_INSPECTION }
            state.step       = 'contracts'
          }),

        getPhotoCount:     () => photoMemory.getPhotoCount(),
        getDamageCount:    () => get().inspection.damages.length,
        getCompletionRate: () => {
          const total = 12
          const done  = photoMemory.getPhotoCount()
          return Math.round((done / total) * 100)
        },
      })),
      {
        name:    'rentalcheck-store-v7',
        version: 7,

        migrate: (persistedState, version) => {
          if (version < 2) {
            const migratePhotos = (photos) => {
              if (!photos) return {}
              return Object.fromEntries(
                Object.entries(photos).map(([k, v]) => [
                  k,
                  typeof v === 'string' ? { dataUrl: v, originalDataUrl: v } : v,
                ])
              )
            }
            if (persistedState.inspection) {
              persistedState.inspection.photos       = migratePhotos(persistedState.inspection.photos)
              persistedState.inspection.departPhotos = migratePhotos(persistedState.inspection.departPhotos)
            }
          }
          if (version < 3 && persistedState.inspection) {
            persistedState.inspection.photosChecked       ??= false
            persistedState.inspection.accidentDeclaration ??= null
          }
          // ✅ v7.0 — Ajouter inspectionKey si absent (migration v6 → v7)
          if (version < 7 && persistedState.inspection) {
            if (!persistedState.inspection.inspectionKey && persistedState.inspection.contract && persistedState.inspection.type) {
              const type = persistedState.inspection.type
              const numero = persistedState.inspection.contract?.numeroDocument
              if (numero && (type === 'depart' || type === 'retour')) {
                const prefix = type === 'depart' ? 'dep' : 'retour'
                persistedState.inspection.inspectionKey = `${prefix}-${numero}`
              }
            }
          }
          return persistedState
        },

        partialize: (state) => ({
          // ⚠️ SECURITÉ : credentials (username + password) sont persistés ici
          // car le backend MRC nécessite user+pass à chaque requête (pas de token).
          // TODO: Implémenter un flow session/token côté backend pour éliminer
          // la persistance du password dans localStorage.
          credentials:     state.credentials,
          isAuthenticated: state.isAuthenticated,
          selectedAgency:  state.selectedAgency,
          // ✅ FIX QUOTA iOS — contracts EXCLUS du localStorage (trop lourd : 78+ contrats).
          // Ils sont refetchés au démarrage via l'API MRC. Ne jamais remettre ici.
          lastSync:        state.lastSync,
          step: ['done', 'pdf', 'queue'].includes(state.step) ? 'contracts' : state.step,
          inspection: {
            contract:            state.inspection.contract,
            type:                state.inspection.type,
            inspectionKey:       state.inspection.inspectionKey,
            damages:             state.inspection.damages,
            mileage:             state.inspection.mileage,
            fuel:                state.inspection.fuel,
            // ✅ FIX QUOTA iOS — signatures base64 EXCLUES du localStorage.
            // Les signatures sont sauvegardées dans IndexedDB (saveDraft) — pas besoin ici.
            signatureAgent:      null,
            signatureClient:     null,
            completedAt:         state.inspection.completedAt,
            photosChecked:       state.inspection.photosChecked,
            accidentDeclaration: state.inspection.accidentDeclaration,
            startedAt:           state.inspection.startedAt,
            deviceInfo:          state.inspection.deviceInfo,
            pendingPhotoSlot:    state.inspection.pendingPhotoSlot,
            pendingPhotoData:    null,
            securityCheck:       state.inspection.securityCheck,
          },
          completedInspections: state.completedInspections,
          // ✅ FIX QUOTA iOS — uploadQueue sans signatures base64 (trop lourdes).
          // Les signatures sont dans le draft IndexedDB (saveImmediate).
          uploadQueue: state.uploadQueue.map(item => ({
            ...item,
            inspection: item.inspection ? {
              ...item.inspection,
              signatureAgent:  null,
              signatureClient: null,
            } : item.inspection,
          })),
          selectionQueue:       state.selectionQueue,
        }),
      }
    )
  )
)

// ── Enregistre le store dans le module auth (évite require() sous Vite/ESM)
registerAppStore(useAppStore.getState)
